// Mirrors iOS RestaurantAnalyticsView: metric cards, Revenue Overview card with
// timeframe picker + chart-type chips, a chart per type (expandable via Sheet),
// Daily Earnings Calendar link, earnings history toggle, popular items,
// chart details, and order history.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag, BarChart3, XCircle, CalendarClock, ChevronRight, ChevronDown,
  ChevronUp, Clock, TrendingUp, Star, LayoutGrid, DollarSign, ArrowUp, ArrowDown,
  Info, CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import HelpSheet from '../shared/ui/HelpSheet';
import { SegmentedPicker, StatCard, LoadingView } from '../shared/ui';
import { listenToRestaurantOrders } from '../shared/services/orderService';
import { listenToMenuItems } from '../shared/services/userService';
import { formatCurrency } from '../shared/models';
import {
  TIMEFRAMES, ordersInTimeframe, computeSummary, computeGrowth, hourlySales,
  revenueByDay, topItems, categoryPerformance, weeklyTrends, dailyEarningsMap,
  formatHourFull, fmtMoney,
} from '../components/analytics/analyticsUtils';
import ChartCard, { NoData } from '../components/analytics/ChartCard';
import {
  HourlySalesChart, RevenueTrendChart, DailyRevenueChart, TopItemsChart,
  CategoryPerformanceChart, WeeklyTrendsChart,
} from '../components/analytics/AnalyticsCharts';
import ChartDetailSection from '../components/analytics/ChartDetailSection';
import OrderHistorySection from '../components/analytics/OrderHistorySection';

const CHART_TYPES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'topItems', label: 'Top Items' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'categories', label: 'Categories' },
  { value: 'weeklyTrends', label: 'Weekly Trends' },
];

const CHART_META = {
  hourly: { title: 'Hourly Sales', empty: 'No hourly data available', EmptyIcon: Clock },
  daily: { title: 'Daily Revenue', empty: 'No revenue data available', EmptyIcon: DollarSign },
  topItems: { title: 'Top Items', sub: 'Best sellers by orders placed', empty: 'No item data available', EmptyIcon: ShoppingBag },
  revenue: { title: 'Revenue Trends', empty: 'No revenue data available', EmptyIcon: TrendingUp },
  categories: { title: 'Category Performance', empty: 'No category data available', EmptyIcon: LayoutGrid },
  weeklyTrends: { title: 'Weekly Trends', empty: 'No weekly data available', EmptyIcon: CalendarClock },
};

function GrowthBadge({ value }) {
  if (!value) return null;
  const up = value >= 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-green-600' : 'text-red-500'}`}>
      <Icon size={11} />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

export default function AnalyticsPage() {
  const { restaurantId } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('year');
  const [chartType, setChartType] = useState('topItems');
  const [showEarningsHistory, setShowEarningsHistory] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!restaurantId) { setLoading(false); return undefined; }
    const unsub = listenToRestaurantOrders(restaurantId, (list) => {
      setOrders(list);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) return undefined;
    return listenToMenuItems(restaurantId, setMenuItems, () => {});
  }, [restaurantId]);

  const filtered = useMemo(() => ordersInTimeframe(orders, timeframe), [orders, timeframe]);
  const summary = useMemo(() => computeSummary(filtered), [filtered]);
  const growth = useMemo(() => computeGrowth(orders, timeframe), [orders, timeframe]);
  const hourly = useMemo(() => hourlySales(filtered), [filtered]);
  const daily = useMemo(() => revenueByDay(filtered), [filtered]);
  const top = useMemo(() => topItems(filtered), [filtered]);
  const categories = useMemo(() => categoryPerformance(filtered, menuItems), [filtered, menuItems]);
  const weekly = useMemo(() => weeklyTrends(orders), [orders]);
  const earningsByDay = useMemo(() => dailyEarningsMap(orders), [orders]);
  const earningsHistory = useMemo(
    () => [...earningsByDay.values()].sort((a, b) => b.date - a.date),
    [earningsByDay],
  );

  const hourlyRows = hourly
    .filter((d) => d.hour >= 7 || d.hour === 0)
    .sort((a, b) => (a.hour === 0 ? 24 : a.hour) - (b.hour === 0 ? 24 : b.hour))
    .map((d) => ({ label: formatHourFull(d.hour), orders: d.orders, revenue: d.revenue }));
  const peakHourRow = hourlyRows.reduce((m, d) => (d.revenue > (m?.revenue ?? -1) ? d : m), null);
  const peakDay = daily.reduce((m, d) => (d.revenue > (m?.revenue ?? -1) ? d : m), null);
  const weeklyTotal = weekly.reduce((s, d) => s + d.revenue, 0);

  const chartContent = {
    hourly: {
      empty: hourlyRows.every((d) => d.revenue === 0),
      chart: <HourlySalesChart data={hourly} />,
      right: peakHourRow?.revenue > 0 && (
        <span className="text-[11px] text-gray-400">Peak: <b className="text-gray-700">{peakHourRow.label}</b></span>
      ),
      detail: {
        chart: <HourlySalesChart data={hourly} height={320} />,
        columns: [
          { key: 'label', label: 'Hour' },
          { key: 'orders', label: 'Orders', align: 'right' },
          { key: 'revenue', label: 'Revenue', align: 'right', money: true },
        ],
        rows: hourlyRows.filter((d) => d.revenue > 0 || d.orders > 0),
      },
    },
    daily: {
      empty: daily.length === 0,
      chart: <DailyRevenueChart data={daily} />,
      right: peakDay && (
        <span className="text-[11px] text-gray-400">Peak: <b className="text-gray-700">{peakDay.label}</b></span>
      ),
      detail: {
        chart: <DailyRevenueChart data={daily} height={320} />,
        columns: [
          { key: 'label', label: 'Date' },
          { key: 'orders', label: 'Orders', align: 'right' },
          { key: 'revenue', label: 'Revenue', align: 'right', money: true },
        ],
        rows: daily,
      },
    },
    revenue: {
      empty: daily.length === 0,
      chart: <RevenueTrendChart data={daily} />,
      right: peakDay && (
        <span className="text-[11px] text-gray-400">Peak: <b className="text-gray-700">{peakDay.label}</b></span>
      ),
      detail: {
        chart: <RevenueTrendChart data={daily} height={320} />,
        columns: [
          { key: 'label', label: 'Date' },
          { key: 'orders', label: 'Orders', align: 'right' },
          { key: 'revenue', label: 'Revenue', align: 'right', money: true },
        ],
        rows: daily,
      },
    },
    topItems: {
      empty: top.length === 0,
      chart: <TopItemsChart data={top} />,
      detail: {
        chart: <TopItemsChart data={top} height={340} />,
        columns: [
          { key: 'name', label: 'Item' },
          { key: 'quantity', label: 'Qty', align: 'right' },
          { key: 'revenue', label: 'Revenue', align: 'right', money: true },
        ],
        rows: top,
      },
    },
    categories: {
      empty: categories.length === 0,
      chart: <CategoryPerformanceChart data={categories} />,
      detail: {
        chart: <CategoryPerformanceChart data={categories} height={340} />,
        columns: [
          { key: 'name', label: 'Category' },
          { key: 'orders', label: 'Orders', align: 'right' },
          { key: 'revenue', label: 'Revenue', align: 'right', money: true },
        ],
        rows: categories,
      },
    },
    weeklyTrends: {
      empty: weekly.every((d) => d.revenue === 0 && d.orders === 0),
      chart: <WeeklyTrendsChart data={weekly} />,
      right: weeklyTotal > 0 && (
        <span className="text-[11px] text-gray-400">Total: <b className="text-gray-700">{fmtMoney(weeklyTotal)}</b></span>
      ),
      detail: {
        chart: <WeeklyTrendsChart data={weekly} height={320} />,
        columns: [
          { key: 'week', label: 'Day' },
          { key: 'orders', label: 'Orders', align: 'right' },
          { key: 'revenue', label: 'Revenue', align: 'right', money: true },
        ],
        rows: weekly,
      },
    },
  };

  const active = chartContent[chartType];
  const meta = CHART_META[chartType];

  return (
    <div className="pb-6">
      <ChefHeader
        title="Analytics"
        right={
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowHelp(true)}
              aria-label="Analytics help"
              className="h-9 px-2 rounded-full flex items-center gap-1 text-blue-500 active:bg-blue-50"
            >
              <Info size={18} />
              <span className="text-sm font-semibold hidden sm:inline">Analytics Info</span>
            </button>
            <button
              onClick={() => navigate('/business/earnings')}
              className="flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 active:bg-red-100"
            >
              <span className="hidden min-[380px]:inline">Payout Calendar</span>
              <span className="min-[380px]:hidden">Payout</span>
            </button>
          </div>
        }
      />

      {loading ? (
        <LoadingView label="Loading analytics…" />
      ) : (
        <div className="px-4 pt-4 space-y-5">
          {/* Key metric cards (iOS LazyVGrid 2-col) */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label="Revenue"
              value={formatCurrency(summary.revenue)}
              icon={DollarSign}
              color="text-green-600"
              sub={growth.revenueGrowth ? `${growth.revenueGrowth >= 0 ? '▲' : '▼'} ${Math.abs(growth.revenueGrowth).toFixed(1)}% vs prev.` : null}
            />
            <StatCard
              label="Orders"
              value={`${summary.orders}`}
              icon={ShoppingBag}
              color="text-blue-600"
              sub={growth.orderGrowth ? `${growth.orderGrowth >= 0 ? '▲' : '▼'} ${Math.abs(growth.orderGrowth).toFixed(1)}% vs prev.` : null}
            />
            <StatCard
              label="Avg Order"
              value={formatCurrency(summary.avgOrder)}
              icon={BarChart3}
              color="text-[#FF6B35]"
              sub={summary.completed ? `${summary.completed} completed` : null}
            />
            <StatCard
              label="Cancelled"
              value={`${summary.cancelled}`}
              icon={XCircle}
              color="text-red-500"
              sub={`${summary.completionRate.toFixed(1)}% completion`}
            />
          </div>

          {/* Revenue Overview card: totals + timeframe + chart-type chips */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-bold text-gray-900 pt-1">Revenue Overview</h3>
              <div className="flex items-start gap-4">
                <div className="text-right">
                  <p className="text-base font-extrabold text-gray-900">{formatCurrency(summary.revenue)}</p>
                  <GrowthBadge value={growth.revenueGrowth} />
                </div>
                <div className="text-right">
                  <p className="text-base font-extrabold text-gray-900">{summary.orders}</p>
                  <p className="text-[11px] text-gray-400">orders</p>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <SegmentedPicker options={TIMEFRAMES} value={timeframe} onChange={setTimeframe} />
            </div>

            <div className="border-t border-gray-100 my-3" />

            {/* Chart type chips (iOS: horizontal capsules, blue when active);
                on wide screens they stretch edge to edge on white */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 py-0.5 lg:mx-0 lg:px-0">
              {CHART_TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setChartType(t.value)}
                  className={`shrink-0 lg:shrink lg:flex-1 whitespace-nowrap px-3 py-1.5 lg:py-2.5 rounded-full text-xs lg:text-sm font-semibold transition-colors border ${
                    chartType === t.value
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-gray-100 lg:bg-white text-gray-700 border-transparent lg:border-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Selected chart — its own card, tap header to expand into a Sheet */}
          <ChartCard
            title={meta.title}
            subtitle={meta.sub || null}
            right={active.right || null}
            detail={active.detail}
          >
            {active.empty
              ? <NoData message={meta.empty} icon={meta.EmptyIcon} />
              : active.chart}
          </ChartCard>

          {/* Payments link (iOS RestaurantPaymentsView) */}
          <button
            onClick={() => navigate('/business/payments')}
            className="w-full flex items-center gap-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-left active:bg-gray-50"
          >
            <DollarSign size={18} className="text-green-600" />
            <span className="flex-1 text-sm font-semibold text-gray-900">View Payments</span>
            <ChevronRight size={16} className="text-gray-400" />
          </button>

          {/* Earnings History toggle */}
          <button
            onClick={() => setShowEarningsHistory((s) => !s)}
            className="w-full flex items-center gap-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-left active:bg-gray-50"
          >
            <BarChart3 size={18} className="text-green-600" />
            <span className="flex-1 text-sm font-semibold text-gray-900">
              {showEarningsHistory ? 'Hide Earnings History' : 'View Earnings History'}
            </span>
            {showEarningsHistory
              ? <ChevronUp size={16} className="text-gray-500" />
              : <ChevronDown size={16} className="text-gray-500" />}
          </button>

          {showEarningsHistory && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
              <h3 className="text-sm font-bold text-gray-900">Daily Earnings History</h3>
              {earningsHistory.length === 0 ? (
                <NoData message="Earnings from orders will appear here." icon={BarChart3} />
              ) : (
                <>
                  <DailyRevenueChart
                    data={[...earningsHistory.slice(0, 30)].reverse()
                      .map((d) => ({
                        label: d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        revenue: d.totalEarnings,
                        orders: d.orderCount,
                      }))}
                  />
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500">Daily Breakdown</p>
                    {earningsHistory.slice(0, 30).map((d) => (
                      <div key={d.date.getTime()} className="flex items-center justify-between bg-gray-50/70 rounded-lg px-3 py-2">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          <p className="text-[11px] text-gray-400">
                            {d.date.toLocaleDateString('en-US', { weekday: 'long' })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-green-600">{formatCurrency(d.totalEarnings)}</p>
                          <p className="text-[11px] text-gray-400">{d.orderCount} orders</p>
                        </div>
                      </div>
                    ))}
                    {earningsHistory.length > 30 && (
                      <p className="text-[11px] text-gray-400 text-center">Showing 30 of {earningsHistory.length} days</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Popular Items (iOS: top 5) */}
          {top.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-1">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Popular Items</h3>
              {top.slice(0, 5).map((item) => (
                <div key={item.name} className="flex items-center justify-between py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                    <p className="text-[11px] text-gray-400">{item.quantity} sold</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900 shrink-0">{formatCurrency(item.revenue)}</p>
                </div>
              ))}
            </div>
          )}

          <ChartDetailSection chartType={chartType} />

          <OrderHistorySection orders={orders} />
        </div>
      )}

      {/* iOS "Reading Analytics" HowToGuideView */}
      <HelpSheet
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Reading Analytics"
        subtitle="What each number on this page means."
        steps={[
          { Icon: ShoppingBag, title: 'Orders', detail: 'Total orders in the selected period. The arrow compares it to the previous period.' },
          { Icon: BarChart3, title: 'Avg Order', detail: 'Average amount a customer spends per order. Higher means bigger baskets.' },
          { Icon: CheckCircle2, title: 'Completed', detail: 'Orders you fulfilled successfully.' },
          { Icon: XCircle, title: 'Cancelled', detail: 'Orders that were rejected or cancelled — keep this low.' },
          { Icon: DollarSign, title: 'Revenue', detail: 'Your payout share from completed orders. The trend chart shows whether sales are growing.' },
          { Icon: Star, title: 'Top Items & Categories', detail: 'Your best sellers — feature them, keep them stocked, and price similar items around them.' },
        ]}
      />
    </div>
  );
}
